// MARK://dhk8/pos/lib/gql
// Cliente GraphQL (urql) del POS — apunta al graphql-gateway vía el rest-proxy
// (`${apiOrigin()}/graphql`, MISMO origin que el REST → reusa CORS).
//
// Reusa el auth del POS (lib/api.ts): inyecta `Authorization: Bearer <jwt>` +
// `x-dhk8-tenant-id` y, ante un 401, rota el access token (single-flight) con
// el mismo endpoint `/api/auth/refresh`. `preferGetMethod:false` porque urql
// manda las queries como GET y el gateway (Apollo) las bloquea por CSRF.
//
// Refs: ADR-0002 (gateway = puerta única), ADR-0003 (auth/RLS).
import { Client, fetchExchange, cacheExchange } from 'urql'
import { authExchange } from '@urql/exchange-auth'
import { apiOrigin } from './api-origin.js'
import {
  getStoredJwt, getStoredRefreshJwt, setStoredJwt, setStoredRefreshJwt,
  getTenantId, svcUrl,
} from './api.js'

const GQL_URL = `${apiOrigin()}/graphql`

let refreshInFlight: Promise<string | null> | null = null
async function doRefresh(): Promise<string | null> {
  const rt = getStoredRefreshJwt()
  if (!rt) return null
  try {
    const r = await fetch(svcUrl('dhk8-platform-auth', '/api/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
      credentials: 'include',
    })
    if (!r.ok) return null
    const raw = (await r.json()) as
      | { ok: true; data: { token?: string; refreshToken?: string } }
      | { token?: string; refreshToken?: string }
    const d = raw && typeof raw === 'object' && 'data' in raw ? raw.data : (raw as { token?: string; refreshToken?: string })
    if (d && typeof d.token === 'string' && d.token.length > 0) {
      setStoredJwt(d.token)
      if (typeof d.refreshToken === 'string' && d.refreshToken.length > 0) setStoredRefreshJwt(d.refreshToken)
      return d.token
    }
    return null
  } catch {
    return null
  }
}
function ensureRefreshed(): Promise<string | null> {
  if (refreshInFlight === null) {
    refreshInFlight = doRefresh().finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

function withAuth(init: RequestInit | undefined, token: string | null): RequestInit {
  const tenantId = getTenantId()
  // urql pasa `init.headers` como `Headers`; mergear con `new Headers` preserva
  // el `content-type: application/json` (si no, Apollo lo bloquea como CSRF).
  const headers = new Headers(init?.headers ?? {})
  if (tenantId) {
    headers.set('x-dhk8-tenant-id', tenantId)
    headers.set('X-Tenant-Id', tenantId)
  }
  if (token) headers.set('Authorization', `Bearer ${token}`)
  return { ...init, credentials: 'include', headers }
}

const gqlFetch: typeof fetch = async (input, init) => {
  let res = await fetch(input, withAuth(init, getStoredJwt()))
  if (res.status === 401) {
    const fresh = await ensureRefreshed()
    if (fresh) res = await fetch(input, withAuth(init, fresh))
  }
  return res
}

// authExchange: refresca también ante errores de auth a NIVEL GraphQL (200 +
// errors con code UNAUTHENTICATED, no 401 de transporte). didAuthError detecta
// → refreshAuth (mismo single-flight) → urql reintenta; el retry re-lee el token
// rotado vía gqlFetch. Ref: ADR-0003.
const auth = authExchange(async () => ({
  addAuthToOperation: (operation) => operation,
  didAuthError: (error) =>
    error.graphQLErrors.some(
      (e) =>
        (e.extensions as { code?: unknown } | undefined)?.code === 'UNAUTHENTICATED' ||
        /tenant scope required|present a valid JWT|UNAUTHENTICATED/i.test(e.message),
    ),
  refreshAuth: async () => {
    await ensureRefreshed()
  },
}))

export const gqlClient = new Client({
  url: GQL_URL,
  preferGetMethod: false,
  // 🔴 `network-only` NO es opcional aquí.
  //
  // Sin `requestPolicy` urql usa `cache-first`: la 1a consulta va a la red y
  // TODAS las siguientes con las mismas variables salen de su caché, para
  // siempre. Todo el POS consume el grafo con `gqlClient.query(...).toPromise()`
  // detrás de react-query, así que el refetch de react-query (staleTime /
  // refetchInterval / invalidateQueries) llamaba al cliente… y urql devolvía la
  // foto vieja sin tocar la red. Efecto observado en prod: abrir una mesa la
  // dejaba pintada de "Disponible" indefinidamente aunque la BD, el API y hasta
  // el evento WS dijeran OCCUPIED — no era el plano ni el polling, era que el
  // dato jamás se volvía a pedir.
  //
  // `cache-and-network` NO arregla este uso: `.toPromise()` resuelve con el
  // PRIMER resultado, que sigue siendo el de caché.
  //
  // Quien cachea aquí es react-query (staleTime, dedupe, invalidación); la
  // caché de urql sólo aporta staleness. Ver ADR-0009.
  requestPolicy: 'network-only',
  exchanges: [cacheExchange, auth, fetchExchange],
  fetch: gqlFetch,
})

// Hook de smoke/diagnóstico (solo dev).
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  ;(window as unknown as { __gqlClient?: Client }).__gqlClient = gqlClient
}
